import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';

import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { BodyMicro } from 'components/Text/BodyMicro';
import { useUser } from 'contexts/UserEnvironmentContext';
import type { SearchAlgoliaImportedHit } from 'modules/search/algolia/types/SearchAlgoliaImportedHit';
import { SearchHitImage } from 'modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from 'modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
} from 'modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from 'modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import { getImportedListingUrl } from 'modules/search/helpers/getImportedListingUrl';
import type { SearchSavedListingsStore } from 'modules/search/zustand-stores/searchSavedListingsStore';
import { getWorkLocationString } from 'utils/address/getWorkLocationString';
import { VOLOP_IMPORTED_TYPE_MAP } from 'utils/constants/general/volopImportedTypeMap';

import { SearchImportedHitInfo } from './SearchImportedHitInfo';

type Props = {
  hit: SearchAlgoliaImportedHit;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  hideOwnerBanner: boolean;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  actions: ReactElement | null;
};

export function SearchImportedHit({
  hit,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  hideOwnerBanner,
  highlighted,
  application,
  actions,
}: Props) {
  const { user } = useUser();
  const {
    name,
    city,
    state,
    country,
    url,
    startDate,
    startTime,
    endDate,
    endTime,
    endsLocal,
    startsLocal,
    timezone,
    objectID,
    orgID,
    orgType,
    source,
    locationType,
    remoteZone,
    logo,
    imageUrl,
    imageHandle,
    published,
    isPostedAnonymously,
    siteName,
  } = hit;

  const imageHandleImage = imageHandle ? { handle: imageHandle } : null;

  const location = useMemo(
    () =>
      getWorkLocationString({
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [remoteZone, city, country, state],
  );

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published && source && ['DO_SOMETHING'].includes(source)) {
      stats.push(
        /* TRANSLATORS Ongoing */
        <BodyMicro lineHeight={1}>{getText('Ongoing')}</BodyMicro>,
      );
    }

    if (source) {
      stats.push(
        <BodyMicro lineHeight={1}>{VOLOP_IMPORTED_TYPE_MAP[source]}</BodyMicro>,
      );
    }

    return stats;
  }, [published, source]);

  let { orgName } = hit;
  if (source === 'VOLUNTEERGOV') {
    orgName = orgName ? `${siteName} (${orgName})` : `${siteName}`;
  }

  const ownerOrg = user ? user.orgs.find((o) => o.id === orgID) : null;
  const startDateTime = startDate && startTime ? startsLocal : null;
  const endDateTime = endDate && endTime ? endsLocal : null;

  return (
    <SearchHitHolder data-qa-id="search-result" data-hit-id={objectID}>
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          roundedTop={hideOwnerBanner || !ownerOrg}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link',
            href: getImportedListingUrl(url, source),
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile>
            <SearchHitImage
              isImported
              imageUrl={imageUrl}
              image={imageHandleImage}
              source={source}
              name={name}
              logo={logo}
            />
          </SearchHitImageHolder>

          <SearchImportedHitInfo
            application={application}
            endDateTime={endDateTime}
            isPostedAnonymously={isPostedAnonymously}
            listingStats={listingStats}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            showLabel={showLabel}
            startDateTime={startDateTime}
            timezone={timezone}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
